'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { parsePriceInputToCents } from '@mentora/app-shared/price-input'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mentora/ui-components'
import { Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'
import { AmountField, ReasonField } from './transaction-dialog-fields'

// Cancelling a membership: now (the members area closes) or at the end of the
// paid period (it just does not renew), optionally refunding part or all of
// the latest payment. A blank amount refunds nothing.

export type CancelMembershipAction = (input: {
  membershipId: string
  when: 'now' | 'period_end'
  refundAmountCents: number
  reason: string
}) => Promise<ActionResult<{ refund: { state: 'issued' | 'awaiting_rail' } | null }>>

export interface MembershipActionsCardProps {
  membershipId: string
  /** What is left to refund on the latest payment; null when there is none (or Stripe could not be read). */
  refundable: { cents: number, currency: string } | null
  /** Already set to end at period end: only "now" is left to do. */
  endsAtPeriodEnd: boolean
  cancelMembership: CancelMembershipAction
}

export function MembershipActionsCard({ membershipId, refundable, endsAtPeriodEnd, cancelMembership }: MembershipActionsCardProps) {
  const t = useTranslations('transactions.membership')
  const ta = useTranslations('transactions.actions')
  const { showError, showSuccess } = useActionToast()
  const [open, setOpen] = useState(false)
  const [when, setWhen] = useState<'now' | 'period_end'>(endsAtPeriodEnd ? 'now' : 'period_end')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [isPending, startTransition] = useTransition()

  const refundCents = amount.trim() === '' ? 0 : parsePriceInputToCents(amount)
  const valid = refundCents !== null && refundCents <= (refundable?.cents ?? 0) && reason.trim().length >= 3

  function submit() {
    if (refundCents === null)
      return
    startTransition(async () => {
      const result = await cancelMembership({ membershipId, when, refundAmountCents: refundCents, reason: reason.trim() })
      if (!result.ok) {
        showError(result)
        return
      }
      showSuccess(result.data?.refund?.state === 'awaiting_rail' ? ta('toast.awaitingRail') : t('toast.cancelled'))
      setOpen(false)
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{ta('title')}</CardTitle>
        <CardDescription>{t('actionsDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => setOpen(true)}>{t('cancelButton')}</Button>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('cancelTitle')}</DialogTitle>
              <DialogDescription>{t('cancelDescription')}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <div className="space-y-2">
                <Label htmlFor={`membership-when-${membershipId}`}>{t('whenLabel')}</Label>
                <Select value={when} onValueChange={value => setWhen(value === 'now' ? 'now' : 'period_end')}>
                  <SelectTrigger id={`membership-when-${membershipId}`} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!endsAtPeriodEnd && <SelectItem value="period_end">{t('whenPeriodEnd')}</SelectItem>}
                    <SelectItem value="now">{t('whenNow')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {refundable && refundable.cents > 0
                ? <AmountField id={`membership-refund-${membershipId}`} value={amount} onChange={setAmount} maxCents={refundable.cents} currency={refundable.currency} />
                : <p className="text-sm text-muted-foreground">{t('nothingToRefund')}</p>}
              <ReasonField id={`membership-reason-${membershipId}`} value={reason} onChange={setReason} />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="ghost" disabled={isPending}>{ta('close')}</Button>
              </DialogClose>
              <Button onClick={submit} disabled={isPending || !valid}>
                {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('cancelConfirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
